import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon, ZoomInIcon } from 'lucide-react';
import { samples } from '../data/samples';
import { Container } from './Container';
import { Highlight, SectionHeading } from './SectionHeading';
type Sample = (typeof samples)[number];
export function SamplesSection() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [zoomed, setZoomed] = useState<Sample | null>(null);
  useEffect(() => {
    if (!zoomed) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setZoomed(null);
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [zoomed]);
  const scrollToIndex = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(samples.length - 1, index));
    const item = track.children[clamped] as HTMLElement | undefined;
    if (item) track.scrollTo({
      left: item.offsetLeft - track.offsetLeft,
      behavior: 'smooth'
    });
  };
  const handleScroll = () => {
    const track = trackRef.current;
    const first = track?.children[0] as HTMLElement | undefined;
    if (!track || !first) return;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    setActive(atEnd ? samples.length - 1 : Math.round(track.scrollLeft / (first.offsetWidth + 16)));
  };
  const arrowClass = 'grid h-11 w-11 place-items-center rounded-full border border-line bg-white text-navy transition-[background-color,opacity] duration-150 ease-out hover:bg-teal-soft disabled:opacity-30 sm:h-12 sm:w-12';
  return <section className="bg-white py-14 sm:py-20 lg:py-28" data-id="element-129" data-mp="eqdisfkfunjk">
      <Container data-id="element-130" data-mp="jmuafjqujk1e">
        <SectionHeading eyebrow="Sample pages" title={<>
              Take a look <Highlight data-id="element-132" data-mp="1mz2yu9bzw60x">inside the lessons</Highlight>
            </>} intro="A selection of real pages from Unit 01. Swipe to browse and tap any page to read it full size." data-id="element-131" data-mp="jof3t61fcwxo" />

        <div ref={trackRef} onScroll={handleScroll} className="no-scrollbar -mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-5 px-5 pb-2 sm:mx-0 sm:scroll-px-0 sm:px-0" aria-label="Sample pages carousel" data-id="element-133" data-mp="1w0spve1yt2y6">
          {samples.map(sample => <figure key={sample.title} className="w-[76%] shrink-0 snap-start sm:w-[calc((100%-16px)/2)] lg:w-[calc((100%-48px)/4)]" data-id="element-134" data-mp="20y2fout9ngmj">
              <button type="button" onClick={() => setZoomed(sample)} className="group relative block aspect-[9/16] w-full overflow-hidden rounded-xl border border-line bg-white shadow-[0_12px_30px_rgba(23,50,77,0.08)]" aria-label={`Enlarge ${sample.title}`} data-id="element-135" data-mp="2aodujcehna87">
                <img src={sample.image} alt={sample.title} loading="lazy" className="h-full w-full object-cover object-top" data-id="element-136" data-mp="33bwzpp4ela9k" />
                <span className="absolute bottom-3 right-3 grid h-9 w-9 place-items-center rounded-full bg-navy/85 text-white transition-transform duration-150 ease-out group-hover:scale-105" data-id="element-137" data-mp="387m3y5rdbfc7">
                  <ZoomInIcon aria-hidden="true" className="h-4 w-4" data-id="element-138" data-mp="2y9o7ww39oe91" />
                </span>
              </button>
              <figcaption className="mt-3" data-id="element-139" data-mp="2ki9m6hhz5yya">
                <span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-teal" data-id="element-140" data-mp="2pb2u1e8qpnel">{sample.label}</span>
                <span className="block font-condensed text-lg font-bold uppercase leading-tight text-navy sm:text-xl" data-id="element-141" data-mp="2anhozesb1e41">{sample.title}</span>
              </figcaption>
            </figure>)}
        </div>

        <div className="mt-7 flex items-center justify-center gap-5 sm:mt-8" data-id="element-142" data-mp="21m1obyrakqd0">
          <button type="button" onClick={() => scrollToIndex(active - 1)} disabled={active === 0} className={arrowClass} aria-label="Previous sample" data-id="element-143" data-mp="25ljyq0lhlj1l">
            <ChevronLeftIcon className="h-5 w-5" data-id="element-144" data-mp="1tkk7sc5jxwps" />
          </button>
          <div className="flex gap-2" aria-hidden="true" data-id="element-145" data-mp="1tlc4jqavy0hn">
            {samples.map((sample, i) => <span key={sample.title} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${i === active ? 'w-6 bg-teal' : 'w-2 bg-line'}`} data-id="element-146" data-mp="1orr5duxk30gv" data-mpi={i} />)}
          </div>
          <button type="button" onClick={() => scrollToIndex(active + 1)} disabled={active >= samples.length - 1} className={arrowClass} aria-label="Next sample" data-id="element-147" data-mp="1att3hp0f8wi9">
            <ChevronRightIcon className="h-5 w-5" data-id="element-148" data-mp="10xprzsjojlin" />
          </button>
        </div>
        <p className="mt-5 text-center text-xs text-muted" data-id="element-149" data-mp="307plujv2cos4">A preview only — the complete book is included in the bundle.</p>
      </Container>

      <AnimatePresence data-id="element-150" data-mp="2v8390bj5daju">
        {zoomed && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="fixed inset-0 z-50 overflow-y-auto bg-navy/95 px-3 py-16 sm:px-6" onClick={() => setZoomed(null)} role="dialog" aria-modal="true" aria-label={zoomed.title} data-id="element-151" data-mp="33pvgxv166zi2">
            <button type="button" onClick={() => setZoomed(null)} className="fixed right-3 top-3 z-10 grid h-11 w-11 place-items-center rounded-full bg-white text-navy shadow-lg" aria-label="Close" data-id="element-152" data-mp="2ku5de3dl9v22">
              <XIcon className="h-5 w-5" data-id="element-153" data-mp="2ysmtpg16059y" />
            </button>
            <motion.img initial={{
          scale: 0.96
        }} animate={{
          scale: 1
        }} exit={{
          scale: 0.96
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} src={zoomed.image} alt={zoomed.title} className="mx-auto block h-auto w-full max-w-[720px] rounded-lg bg-white" data-id="element-154" data-mp="931c173i2y3a" />
          </motion.div>}
      </AnimatePresence>
    </section>;
}